import { fabric } from 'fabric';

export type CardFaceBounds = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type ConstrainedImage = fabric.Image & { __cardFaceMoveConstraint?: () => void };

export const addCardFaceImageMoveConstraint = (image: fabric.Image, bounds: CardFaceBounds) => {
  const constrained = image as ConstrainedImage;

  // Remove a previously-registered constraint so re-constraining the same
  // image instance does not leak or duplicate `moving` listeners.
  if (constrained.__cardFaceMoveConstraint) {
    image.off('moving', constrained.__cardFaceMoveConstraint);
  }

  const clamp = () => {
    image.setCoords();
    // Absolute, freshly-calculated bounding rect (accounts for scale + angle).
    const rect = image.getBoundingRect(true, true);

    let deltaX = 0;
    let deltaY = 0;

    if (rect.left < bounds.left) {
      deltaX = bounds.left - rect.left;
    } else if (rect.left + rect.width > bounds.left + bounds.width) {
      // If the image is wider than the face, the left branch above already ran
      // (rect.left < bounds.left) on later ticks; here it is narrower and past
      // the right edge, so pull it back in.
      deltaX = bounds.left + bounds.width - (rect.left + rect.width);
    }

    if (rect.top < bounds.top) {
      deltaY = bounds.top - rect.top;
    } else if (rect.top + rect.height > bounds.top + bounds.height) {
      deltaY = bounds.top + bounds.height - (rect.top + rect.height);
    }

    if (deltaX !== 0 || deltaY !== 0) {
      image.set({
        left: (image.left ?? 0) + deltaX,
        top: (image.top ?? 0) + deltaY,
      });
      image.setCoords();
    }
  };

  const unregister = () => {
    image.off('moving', clamp);
    if (constrained.__cardFaceMoveConstraint === clamp) {
      delete constrained.__cardFaceMoveConstraint;
    }
  };

  image.on('moving', clamp);
  constrained.__cardFaceMoveConstraint = clamp;

  return { clamp, unregister };
};
